import React from "react";
import "~/css/DifyAPITest.css";
import DifyActiveRunSection from "~/components/difyAPITest/DifyActiveRunSection";
import DifyInputMethodSection from "~/components/difyAPITest/DifyInputMethodSection";
import DifyRunActionBar from "~/components/difyAPITest/DifyRunActionBar";
import DifyRunHistorySection from "~/components/difyAPITest/DifyRunHistorySection";
import { useDifyAPITestForm } from "~/components/difyAPITest/hooks/useDifyAPITestForm";
import { useBeforeUnload } from "~/components/hooks/useBeforeUnload";
import { useDifyReceptionBatchRunner } from "~/components/difyAPITest/hooks/useDifyReceptionBatchRunner";
import { useDifyAPITestHistory } from "~/components/difyAPITest/hooks/useDifyAPITestHistory";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";

/**
 * DifyAPIテスト画面を表示する
 * @return 画面要素
 */
const DifyAPITestPage: React.FC = () => {
  const {
    inputMethod,
    draft,
    questionList,
    selectedFile,
    setInputMethod,
    setDraft,
    handleFileChange,
    clearCurrentInput,
    buildSubmitPayload,
    inputRef,
    focusInput,
    handlePaste,
    handleKeyDown,
  } = useDifyAPITestForm();
  const { difyAppName, runs, isSubmitting, activeRunId, submitQuestions, cancelRun, clearRuns } =
    useDifyReceptionBatchRunner();
  const { questionTableList, downloadRunCsv } = useDifyAPITestHistory(runs);

  const shouldWarnBeforeUnload =
    isSubmitting || draft.trim().length > 0 || questionList.length > 0 || selectedFile !== null;

  // ページ離脱前の警告を設定 (編集内容がある場合や実行中の場合に警告を表示)
  useBeforeUnload({
    enabled: shouldWarnBeforeUnload,
    message: "未送信の入力または実行中の処理があります。ページを離れてもよろしいですか？",
  });

  // 現在アクティブな実行を取得
  const activeRun = activeRunId ? (runs.find((run) => run.runId === activeRunId) ?? null) : null;

  /**
   * フォーム送信時のハンドラー
   * @param event フォームの送信イベント
   * @return 送信処理の完了
   */
  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // 送信前のデータ整形
    const { payload, validationMessage } = buildSubmitPayload();
    if (!payload) {
      showNotificationDialog(validationMessage ?? "送信内容を確認してください。", "error");
      return;
    }

    try {
      // Dify APIへ質問を送信する
      await submitQuestions(payload);
      showNotificationDialog("Dify APIへの送信が完了しました。", "success");
      // 送信後は入力内容をクリアする
      clearCurrentInput();
    } catch (error) {
      showNotificationDialog("Dify APIへの送信に失敗しました。", "error");
      console.error("Dify APIへの送信に失敗しました:", error);
    }
  };

  return (
    <div className="dify-api-test-page">
      <div className="dify-api-test-header">
        <div className="dify-api-test-header-content">
          <h1 className="dify-api-test-title">Dify API テスト画面</h1>
        </div>
      </div>

      <div className="dify-api-test-main">
        <div className="dify-api-test-main-content">
          <form className="dify-api-test-container" onSubmit={handleFormSubmit}>
            {/* アプリ名 */}
            <div className="dify-registration-row">
              <div className="dify-input-method-group">
                <span className="dify-input-method-label">DifyAPI</span>
                <div className="dify-input-method-options">
                  <span className="dify-input-method-option dify-input-method-option-static">
                    <span className="dify-app-name-value">{difyAppName ?? "取得中..."}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* 質問内容入力(ファイル(csv)orテキスト) */}
            <DifyInputMethodSection
              inputMethod={inputMethod}
              draft={draft}
              questionList={questionList}
              selectedFile={selectedFile}
              setInputMethod={setInputMethod}
              setDraft={setDraft}
              handleFileChange={handleFileChange}
              onClearCurrentInput={clearCurrentInput}
              inputRef={inputRef}
              focusInput={focusInput}
              handlePaste={handlePaste}
              handleKeyDown={handleKeyDown}
            />

            {/* 送信・実行停止・履歴クリア のアクションボタン */}
            <DifyRunActionBar
              isSubmitting={isSubmitting}
              hasRuns={runs.length > 0}
              canCancel={Boolean(activeRunId && isSubmitting)}
              onCancel={() => {
                if (activeRunId) {
                  void cancelRun(activeRunId);
                }
              }}
              onClear={clearRuns}
            />

            {/* 実行中の質問と回答の進捗表示 */}
            <DifyActiveRunSection activeRun={activeRun} />

            {/* 実行履歴表示とCSVダウンロード */}
            <DifyRunHistorySection
              questionTableList={questionTableList}
              onDownloadRunCsv={downloadRunCsv}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default DifyAPITestPage;
